import type { ReactNode } from 'react';
import type { SortKey } from 'hooks/useDataControl';

/**
 * What a column becomes once the table collapses into cards, below `md`.
 *
 * - `title`  the card's heading line
 * - `meta`   a label/value row in the card body — the default when unset
 * - `action` moved to the card's footer row
 * - `hide`   dropped entirely; a nine-column table does not need nine on a phone
 *
 * The point of driving cards off the same `columns` array is that no screen has to
 * describe its data twice.
 */
export type MobileColumnRole = 'title' | 'meta' | 'action' | 'hide';

export type DataTableFormatArgs<T> = {
  value: T[keyof T];
  item: T;
  data: T[];
  /** Index of `item` within `data`, so `data[idx] === item` on both the table and the card path. */
  idx: number;
};

export type Column<T> = {
  field?: keyof T;
  label?: string;
  labelMobile?: string;
  headerClassName?: string;
  cellClassName?: string;
  format?: (args: DataTableFormatArgs<T>) => ReactNode;
  condition?: boolean | (() => boolean);
  sortKey?: SortKey<T>;
  tooltip?: string;
  /** Card-mode role below `md`. Unset behaves as `meta`. */
  mobile?: MobileColumnRole;
};

/**
 * Inlined rather than imported from the dashboard's `ConsolePanel`: this package is
 * shared with the editor app, so it must not reach into app code. Same class list.
 */
export const FOCUS_RING = 'focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none';

export const isColumnVisible = <T>(column: Column<T>): boolean => {
  const { condition = true } = column;
  return typeof condition === 'function' ? condition() : condition;
};

/** The heading a sort control or a card row shows for a column. */
export const columnLabel = <T>(column: Column<T>): string => {
  const { label, field } = column;
  const text = label ?? (field !== undefined ? String(field) : '');
  return text.trim();
};

/** Card rows prefer the short label, which is what `labelMobile` exists for. */
export const columnLabelMobile = <T>(column: Column<T>): string => {
  const { labelMobile } = column;
  return (labelMobile ?? columnLabel(column)).trim();
};

/**
 * An id-ish column makes a poor card heading — "12" tells the reader nothing — so the
 * title fallback skips them. `valid` and friends must not match, hence the explicit
 * camelCase and snake_case boundaries rather than a bare `id$`.
 */
export const isIdColumn = <T>(column: Column<T>): boolean => {
  const names = [column.field !== undefined ? String(column.field) : '', columnLabel(column)];
  return names.some((name) => /^id$/i.test(name) || /[a-z0-9]Id$/.test(name) || /_id$/i.test(name));
};

export const renderColumnValue = <T>(column: Column<T>, item: T, data: T[], idx: number): ReactNode => {
  const { format, field } = column;
  if (!format) return field !== undefined ? (item[field] as ReactNode) : null;
  const value = field !== undefined ? item[field] : (undefined as unknown as T[keyof T]);
  return format({ value, item, data, idx });
};

export type MobileColumnGroups<T> = {
  /** May be absent: a table of nothing but numbers has no sensible heading line. */
  title?: Column<T>;
  meta: Column<T>[];
  actions: Column<T>[];
};

/**
 * Splits the column list into the three parts of a card. `condition` is honoured
 * first, so a column the screen has switched off never reaches a card either.
 */
export const groupColumnsForMobile = <T>(columns: Column<T>[]): MobileColumnGroups<T> => {
  const shown = columns.filter(isColumnVisible).filter((column) => column.mobile !== 'hide');
  const declaredTitle = shown.find((column) => column.mobile === 'title');
  // Fallback: the first column that renders something readable and is not an id.
  const inferredTitle = shown.find((column) => !!column.format && !isIdColumn(column) && column.mobile === undefined);
  const title = declaredTitle ?? inferredTitle;
  const rest = shown.filter((column) => column !== title);

  return {
    title,
    meta: rest.filter((column) => column.mobile !== 'action'),
    actions: rest.filter((column) => column.mobile === 'action'),
  };
};

/**
 * Sort options for card mode. Every `condition`-visible sortable column is offered,
 * including ones marked `hide`: the card drops the value, but losing the ability to
 * order by it would be a real regression against the table.
 */
export const sortableColumns = <T>(columns: Column<T>[]): Column<T>[] => {
  const seen = new Set<string>();
  return columns.filter(isColumnVisible).filter((column) => {
    if (!column.sortKey) return false;
    const key = String(column.sortKey);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};
